import { useEffect, useState } from "react";
import { PageHeader } from "../../components/common/PageHeader";
import { DataTable } from "../../components/common/DataTable";
import { FormModal } from "../../components/common/FormModal";
import { ConfirmDialog } from "../../components/common/ConfirmDialog";
import { StatusBadge } from "../../components/common/StatusBadge";
import { TextField, SelectField } from "../../components/common/Form";
import { Button } from "@/components/ui/button";
import { UsersService } from "../../services";
import { Plus, Pencil, Power } from "lucide-react";
import { toast } from "sonner";
import { dateTime } from "../../utils/format";
import { useCan } from "../../hooks/useCan";

const empty = {
  fullName: "",
  username: "",
  password: "",
  roleName: "BAR_CASHIER",
  email: "",
  whatsappNumber: "",
  contactNumber: "",
};

const roleOptions = [
  { value: "OWNER", label: "Owner" },
  { value: "ADMIN", label: "Admin" },
  { value: "BAR_CASHIER", label: "Bar Cashier" },
  { value: "RES_CASHIER", label: "Restaurant Cashier" },
  { value: "RESTAURANT", label: "Restaurant" },
  { value: "ORDER_MANAGER", label: "Order Manager" },
];

export const UsersPage = () => {
  const can = useCan();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);

    UsersService.list()
      .then(setRows)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(empty);
    setErrors({});
    setOpen(true);
  };

  const openEdit = (u) => {
    setEditingId(u.userId);

    setForm({
      fullName: u.fullName || "",
      username: u.username || "",
      password: "",
      roleName: u.roleName || "BAR_CASHIER",
      email: u.email || "",
      whatsappNumber: u.whatsappNumber || "",
      contactNumber: u.contactNumber || "",
    });

    setErrors({});
    setOpen(true);
  };

  const validate = () => {
    const e = {};

    if (!form.fullName) {
      e.fullName = "Required";
    }

    if (!editingId) {
      if (!form.username) {
        e.username = "Required";
      }

      if (!form.password || form.password.length < 6) {
        e.password = "Min 6 chars";
      }
    }

    if (!form.roleName) {
      e.roleName = "Required";
    }

    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) {
      e.email = "Invalid email";
    }

    if (!form.contactNumber) {
      e.contactNumber = "Required";
    } else if (!/^\d{10}$/.test(form.contactNumber)) {
      e.contactNumber = "Contact number must be 10 digits";
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) {
      return;
    }

    setSaving(true);

    try {
      if (editingId) {
        await UsersService.update(editingId, {
          fullName: form.fullName,
          roleName: form.roleName,
          email: form.email || null,
          whatsappNumber: form.whatsappNumber || null,
          contactNumber: form.contactNumber || null,
        });

        toast.success("User updated");
      } else {
        await UsersService.create({
          ...form,
          email: form.email || null,
          whatsappNumber: form.whatsappNumber || null,
          contactNumber: form.contactNumber || null,
        });

        toast.success("User created");
      }

      setOpen(false);
      setForm(empty);
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to save user");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (u) => {
    const next = u.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";

    try {
      await UsersService.setStatus(u.userId, next);
      load();
      toast.success("Status updated");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed");
    }
  };

  const remove = async () => {
    setBusy(true);

    try {
      await UsersService.remove(confirmDelete.userId);
      toast.success("User deleted");
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Users"
        subtitle="Manage Owner / Admin / Cashier / Restaurant accounts."
        actions={
          can.create && (
            <Button
              onClick={openCreate}
              className="bg-navy-800 hover:bg-navy-900 text-white"
              data-testid="users-add"
            >
              <Plus className="h-4 w-4 mr-2" />
              New User
            </Button>
          )
        }
      />

      <DataTable
        loading={loading}
        rows={rows.map((r) => ({ ...r, key: r.userId }))}
        testId="users-table"
        searchable
        searchKeys={[
          "fullName",
          "username",
          "roleName",
          "email",
          "whatsappNumber",
          "contactNumber",
          "status",
        ]}
        searchPlaceholder="Search users by name, username, role or email…"
        columns={[
          {
            key: "fullName",
            header: "Name",
          },
          {
            key: "username",
            header: "Username",
          },
          {
            key: "roleName",
            header: "Role",
            render: (r) => <StatusBadge value={r.roleName} />,
          },
          {
            key: "email",
            header: "Email",
            render: (r) => r.email || "—",
          },
          {
            key: "createdAt",
            header: "Created",
            render: (r) => dateTime(r.createdAt),
          },
          {
            key: "status",
            header: "Status",
            render: (r) => <StatusBadge value={r.status} />,
          },
          {
            key: "actions",
            header: "",
            render: (r) =>
              can.modify ? (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEdit(r)}
                    data-testid={`user-edit-${r.userId}`}
                    title="Edit"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => toggle(r)}
                    data-testid={`user-toggle-${r.userId}`}
                    title={r.status === "ACTIVE" ? "Deactivate" : "Activate"}
                  >
                    <Power className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ) : (
                <span className="text-slate-300 text-xs">—</span>
              ),
          },
        ]}
      />

      <FormModal
        open={open}
        onOpenChange={setOpen}
        title={editingId ? "Edit user" : "New user"}
        onSubmit={submit}
        loading={saving}
        submitText={editingId ? "Save" : "Create"}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <TextField
            label="Full name"
            required
            value={form.fullName}
            onChange={(v) => setForm({ ...form, fullName: v })}
            error={errors.fullName}
            testId="user-fullName"
          />

          <TextField
            label="Username"
            required
            value={form.username}
            onChange={(v) => setForm({ ...form, username: v })}
            error={errors.username}
            testId="user-username"
            disabled={!!editingId}
          />

          {!editingId && (
            <TextField
              label="Password"
              required
              type="password"
              value={form.password}
              onChange={(v) => setForm({ ...form, password: v })}
              error={errors.password}
              testId="user-password"
            />
          )}

          <SelectField
            label="Role"
            required
            value={form.roleName}
            onChange={(v) => setForm({ ...form, roleName: v })}
            error={errors.roleName}
            options={roleOptions}
            testId="user-role"
          />

          <TextField
            label="Email"
            value={form.email}
            onChange={(v) => setForm({ ...form, email: v })}
            error={errors.email}
            testId="user-email"
          />

          <TextField
            label="WhatsApp"
            value={form.whatsappNumber}
            onChange={(v) => setForm({ ...form, whatsappNumber: v })}
            testId="user-whatsapp"
          />

          <TextField
            label="Contact Number"
            required
            value={form.contactNumber}
            onChange={(v) => setForm({ ...form, contactNumber: v })}
            error={errors.contactNumber}
            testId="user-contactNumber"
          />
        </div>
      </FormModal>

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(v) => !v && setConfirmDelete(null)}
        title="Delete user?"
        description={
          confirmDelete
            ? `“${confirmDelete.fullName}” will be marked INACTIVE and will no longer be able to sign in.`
            : ""
        }
        confirmText="Delete"
        danger
        loading={busy}
        onConfirm={remove}
      />
    </div>
  );
};
